import { useEffect, useState } from 'react';
import { kebabCase } from 'lodash';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { LabelValueField, EditFieldType } from '../../Compounds/DetailPanel/LabelValueField';
import { saveCorrection, Correction } from '../../../store/Correction';
import { getFormattedDate } from '../../../utils/helpers';
import { hasAnalystOrHigherPermissions } from '../../../store/User';
import { DetailPanelParams } from '../../Compounds/DetailPanel/DetailPanel';
import { getRunsSubject, RunStatus, fetchRuns, Run } from '../../../store/Run';

export interface CorrectionDetailsParams {
  data: Correction;
}

export const correctionSections = (data: Correction, runs: Run[]): DetailPanelParams[] => [
  {
    data,
    canEdit:
      data.paidInRunStatus === RunStatus.PROCESSING && hasAnalystOrHigherPermissions()
        ? [
            {
              field: 'paidInRunId',
              type: EditFieldType.SELECT,
              options: runs
            },
            {
              field: 'correctsRunId',
              type: EditFieldType.SELECT,
              options: runs
            },
            {
              field: 'date',
              type: EditFieldType.DATE
            },
            {
              field: 'revenueRecurringCorrection',
              type: EditFieldType.CURRENCY
            },
            {
              field: 'revenueOneTimeCorrection',
              type: EditFieldType.CURRENCY
            },
            {
              field: 'note',
              type: EditFieldType.TEXTAREA
            }
          ]
        : [],
    fieldMap: {
      paidInRunId: 'Paid In',
      correctsRunId: 'Corrects',
      revenueRecurringCorrection: 'Recurring',
      revenueOneTimeCorrection: 'One-Time',
      note: 'Note'
    },
    associationNameFields: {
      paidInRunId: 'paidInRunName',
      correctsRunId: 'correctsRunName'
    },
    order: ['paidInRunId', 'correctsRunId', 'revenueRecurringCorrection', 'revenueOneTimeCorrection', 'note'],
    links: [
      { field: 'customerName', link: `/customer/${data.customerId}`, external: false },
      {
        field: 'combinedAssetNumber',
        link: `/${kebabCase(data.combinedAssetType)}/${data.combinedAssetId}`,
        external: false
      }
    ]
  }
];

export const infoSections = (data: Correction): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: {
      combinedAssetNumber: data.combinedAssetType === 'asset' ? 'Asset' : 'Provisional Asset',
      commissionsStatus: 'Commissions Status',
      customerName: 'Customer',
      salesAgentName: 'Sales Agent',
      supplierName: 'Supplier',
      tsdName: 'TSD',
      actualFirstCommissionRun: 'First Commission Run',
      actualDecommissionedDate: 'Decommission Run'
    },
    order: [
      'combinedAssetNumber',
      'commissionsStatus',
      'customerName',
      'salesAgentName',
      'supplierName',
      'tsdName',
      'actualFirstCommissionRun',
      'actualDecommissionedDate'
    ],
    links: [
      { field: 'customerName', link: `/customer/${data.customerId}`, external: false },
      {
        field: 'combinedAssetNumber',
        link: `/${kebabCase(data.combinedAssetType)}/${data.combinedAssetId}`,
        external: false
      }
    ]
  }
];

export const CorrectionDetails = ({ data }: CorrectionDetailsParams) => {
  const saveAction = (update: Correction) => saveCorrection(update, true);
  const [runs, setRuns] = useState<Run[]>(getRunsSubject().getValue());

  useEffect(() => {
    const subscription = getRunsSubject().subscribe((runs) => {
      setRuns(runs);
    });
    if (getRunsSubject().getValue().length === 0) fetchRuns();

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  return (
    <div
      className="space-y-4"
      data-testid="correction-details-drawer">
      <DetailViewCard
        enableCollapse
        key="correction-detail-card"
        title="Correction">
        <div
          data-testid="correction-detail-card"
          className="min-h-[10rem]">
          <DetailPanelContainer
            key={runs.length}
            data={correctionSections(data, runs)}
            saveAction={(d) => saveAction(d as Correction)}
          />
        </div>
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="info-detail-card"
        title={data.combinedAssetType === 'asset' ? 'Asset' : 'Provisional Asset'}>
        <div
          data-testid="info-detail-card"
          className="min-h-[15rem]">
          <DetailPanelContainer
            data={infoSections(data)}
            saveAction={(d) => saveAction(d as Correction)}
          />
        </div>
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="meta-detail-card"
        title="Meta">
        <div
          className="flex px-4 pt-4 w-full"
          data-testid="meta-detail-card">
          <LabelValueField
            label="Created At"
            value={getFormattedDate(data.createdAt, 'lll')}
            field="createdAt"
          />
          <LabelValueField
            label="Modified At"
            value={getFormattedDate(data.updatedAt, 'lll')}
            field="updatedAt"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Created By"
            value={`${data.createdBy?.firstName || ''} ${data.createdBy?.lastName || ''}`}
            field="createdBy.name"
          />
          <LabelValueField
            label="Modified By"
            value={`${data.updatedBy?.firstName || ''} ${data.updatedBy?.lastName || ''}`}
            field="updatedBy.name"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="ID"
            value={data.id || ''}
            field="id"
          />
        </div>
      </DetailViewCard>
    </div>
  );
};
